import calc from 'calculatorjs'

function readValue (value) {
  return typeof value === 'function' ? value() : value
}

function recordOperation (operations, onOperation, name, detail) {
  operations.push(name)
  onOperation(name, detail)
}

export function isVolumeLightParcel (parcel) {
  return parcel.tnoPriCount >= 5 &&
    (parcel.width / 10) * (parcel.height / 10) * (parcel.length / 10) / 12000 > parcel.weight / 1000
}

export function mergeDeclCargoes ({
  declCargoes,
  jyCargoes,
  tpItemsDeclRealValue,
  exchangeRate,
  onMerged = () => {},
}) {
  jyCargoes.forEach(item => {
    const cargoe = declCargoes.find(current => current.ccName === item.ccName)
    if (!cargoe) {
      const realValueItems = readValue(tpItemsDeclRealValue)
      const currentExchangeRate = readValue(exchangeRate)
      const tpItem = realValueItems.find(current => current.itemCcName === item.ccName)
      let amount = calc(`${item.amount}*${currentExchangeRate}`)
      let count = item.count
      if (amount < 1) {
        amount = 1
        count = Math.ceil(calc(`${item.totalAmount}*${currentExchangeRate}`))
      } else {
        amount = Math.round(amount)
      }
      const newCargoe = {
        ...item,
        customName: item.twName || item.ccName,
        amount,
        count,
        choose: true,
        showListEdit: false,
        declRealValue: tpItem ? 1 : 0,
      }
      if (tpItem) {
        newCargoe.amount = ''
        newCargoe.showListEdit = true
      }
      declCargoes.push(newCargoe)
      onMerged('insert', item, newCargoe)
      return
    }

    // 保留旧实现的 4.5 换算常量与逐项平均时序。
    const totalAmount = calc(`${item.amount} * ${item.count} * 4.5 + ${cargoe.amount} * ${cargoe.count}`)
    cargoe.count += item.count
    cargoe.amount = Math.ceil(calc.div(totalAmount, cargoe.count))
    onMerged('merge', item, cargoe)
  })
  return declCargoes
}

export function resolveFeeRestoreType ({
  isAllIncTax,
  storageFeeType,
  feeRestorePolicy,
  transport,
}) {
  if (isAllIncTax) return 'tax'
  // 空运税型由当前包裹与申报条件决定，不恢复其他运输方式的历史偏好。
  if (transport === 'air' || !storageFeeType) return ''
  return feeRestorePolicy({ storageFeeType, transport }) ? storageFeeType : ''
}

export async function initCreateContext ({
  pclids,
  prepareRequestExtras = {},
  initialDeclCargoes,
  feeRestorePolicy,
  isAlive,
  storageFeeType = '',
  tpItemsDeclRealValue = [],
  exchangeRate,
}, {
  beforePrepare = () => {},
  requestPrepare,
  resetTransient = () => {},
  parsePrepareModel,
  applyPrepareModel,
  afterPrepareApply = () => true,
  notifyVolumeLight = () => {},
  beforeSridReset = () => {},
  resetSrid,
  afterSridReset = () => {},
  isAllIncTax = () => false,
  getTransport = () => '',
  nextTick,
  applyFeeType,
  afterComplete = () => {},
  onOperation = () => {},
}) {
  const operations = []
  const record = (name, detail) => recordOperation(operations, onOperation, name, detail)
  const request = { pclids, ...prepareRequestExtras }

  record('before-prepare', { pclids })
  beforePrepare({ pclids })

  record('prepare-request', request)
  const response = await requestPrepare(request)
  record('prepare-response', { error: response.data.error })
  if (response.data.error) return { status: 'error', operations, response }

  record('alive-before-parse')
  if (!isAlive()) return { status: 'destroyed-before-parse', operations, response }

  record('reset-transient')
  resetTransient()

  record('parse-prepare')
  const prepareModel = await parsePrepareModel(response.data.model)

  record('alive-before-apply')
  if (!isAlive()) return { status: 'destroyed-before-apply', operations, response }

  record('apply-prepare')
  applyPrepareModel(prepareModel)

  record('after-prepare-apply')
  if (!afterPrepareApply(prepareModel)) {
    return { status: 'stopped-after-apply', operations, prepareModel, response }
  }

  const volumeLightParcel = (prepareModel.parcels || []).find(isVolumeLightParcel)
  if (volumeLightParcel) {
    record('notify-volume-light', { pclid: volumeLightParcel.pclid })
    notifyVolumeLight(volumeLightParcel)
  }

  record('before-srid-reset')
  beforeSridReset(prepareModel)

  record('reset-srid')
  resetSrid()

  record('after-srid-reset')
  afterSridReset(prepareModel)

  const declCargoes = readValue(initialDeclCargoes)
  mergeDeclCargoes({
    declCargoes,
    jyCargoes: prepareModel.parcelsInfo.jyCargoes,
    tpItemsDeclRealValue,
    exchangeRate,
    onMerged: (kind, item) => record(`decl-cargo-${kind}`, { ccName: item.ccName }),
  })

  const allIncTax = isAllIncTax()
  const currentStorageFeeType = allIncTax ? '' : readValue(storageFeeType)
  const feeType = resolveFeeRestoreType({
    isAllIncTax: allIncTax,
    storageFeeType: currentStorageFeeType,
    feeRestorePolicy,
    transport: !allIncTax && currentStorageFeeType ? getTransport() : '',
  })
  if (feeType) {
    record('fee-next-tick', { feeType })
    await nextTick()
    record('alive-before-fee-apply')
    if (!isAlive()) return { status: 'destroyed-before-fee-apply', operations, prepareModel, response }
    // 子组件可能在 nextTick 内自动选中空运，恢复前必须重新检查当前线路。
    const finalFeeType = resolveFeeRestoreType({
      isAllIncTax: isAllIncTax(),
      storageFeeType: currentStorageFeeType,
      feeRestorePolicy,
      transport: getTransport(),
    })
    if (finalFeeType) {
      record('apply-fee-type', { feeType: finalFeeType })
      applyFeeType(finalFeeType)
    }
  }

  record('after-complete')
  afterComplete(prepareModel)
  return { status: 'completed', operations, prepareModel, response }
}

export default initCreateContext
